import { useCallback, useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { apiRequest } from "@data/redux/actions/api"
import {
    clearCheckpointConversation,
    setCheckpointConversation,
} from "@data/redux/actions/liveMode"
import { activitiesService } from "@data/services/activitiesService"
import { authService } from "@data/services/authService"
import { getActivityStatus } from "@data/utility/dataUtils"
import { DialogM } from "@common/components/alert/dialog/Dialog"
import { useApiStatus } from "./useApiStatus"
import { useCurrentActivity } from "./useCurrentActivity"

/**
 * Helper to determine if a checkpoint conversation is a prerequisite quiz
 */
function isPrerequisiteQuizConversation(checkpointConversation) {
    return (
        checkpointConversation?.conversation?.trigger_context ===
        "prerequisite_quiz"
    )
}

/**
 * Helper to check if a quiz conversation has finished (ended or tutor_needed)
 */
function isQuizConversationFinished(checkpointConversation) {
    const status = checkpointConversation?.conversation?.status
    return (
        status === "ended" ||
        status === "tutor_needed" ||
        status === "finished"
    )
}

/**
 * Custom hook to manage prerequisite quiz state and interactions for activities.
 *
 * Consolidates:
 * - Quiz gate visibility logic
 * - Quiz conversation status tracking
 * - Data refresh after quiz completion
 * - Quiz start handler
 *
 * @param {Object} activity - The current activity data
 * @returns {Object} Quiz state and handlers
 */
export function usePrerequisiteQuiz(activity) {
    const dispatch = useDispatch()
    const { setCurrentActivity } = useCurrentActivity()

    // Redux state
    const user = useSelector((state) => state.data.currentUser?.object)
    const checkpointConversation = useSelector(
        (state) => state.liveMode?.checkpointConversation
    )
    const quizProgress = useSelector((state) => state.liveMode?.quizProgress)

    // Local state
    const [showQuizGate, setShowQuizGate] = useState(false)
    const [quizLoading, setQuizLoading] = useState(false)

    // Ref to track quiz conversation for data refresh
    const quizConversationIdRef = useRef(null)

    // Derived values
    const isQuizCheckpoint = isPrerequisiteQuizConversation(checkpointConversation)
    const isQuizFinished = isQuizCheckpoint && isQuizConversationFinished(checkpointConversation)
    const isCheckpointActive = checkpointConversation !== null && isQuizCheckpoint

    // Determine quiz gate visibility
    useEffect(() => {
        if (!activity?.id) {
            setShowQuizGate(false)
            return
        }

        // Quiz not attempted if status is 'pending' or undefined
        const quizNotAttempted =
            activity?.quiz_status === "pending" || !activity?.quiz_status

        // Show quiz gate when:
        // 1. Activity has prerequisite quiz
        // 2. Quiz hasn't been attempted yet
        // 3. Quiz conversation is not completed
        const shouldShowGate =
            activity?.has_prerequisite_quiz &&
            quizNotAttempted &&
            !isQuizFinished

        setShowQuizGate(shouldShowGate)
    }, [
        activity?.id,
        activity?.has_prerequisite_quiz,
        activity?.quiz_status,
        isQuizFinished,
    ])

    // Refresh data when quiz conversation finishes
    useEffect(() => {
        const conversationId = checkpointConversation?.conversation?.id

        if (isQuizCheckpoint && conversationId && isQuizFinished) {
            quizConversationIdRef.current = conversationId

            // Delay to ensure backend has processed the quiz results
            const refreshTimer = setTimeout(() => {
                dispatch(apiRequest(authService.getUser))
                if (activity?.id) {
                    dispatch(
                        apiRequest(activitiesService.getActivity, {
                            id: activity.id,
                        })
                    )
                }
            }, 1000)

            // Second refresh for reliability
            const secondRefreshTimer = setTimeout(() => {
                if (activity?.id) {
                    dispatch(
                        apiRequest(activitiesService.getActivity, {
                            id: activity.id,
                        })
                    )
                }
            }, 2000)

            return () => {
                clearTimeout(refreshTimer)
                clearTimeout(secondRefreshTimer)
            }
        }
    }, [
        checkpointConversation?.conversation?.id,
        checkpointConversation?.conversation?.status,
        isQuizCheckpoint,
        isQuizFinished,
        activity?.id,
        dispatch,
    ])

    // Update current activity after user data refresh
    useApiStatus(authService.getUser, null, false, () => {
        if (!quizConversationIdRef.current || !activity?.id) {
            return
        }

        const allActivities = [
            ...(user?.todo_activities || []),
            ...(user?.activities || []),
        ]
        const refreshedActivity = allActivities.find((a) => a.id === activity.id)

        if (refreshedActivity) {
            setCurrentActivity({
                activityData: refreshedActivity,
                progressStatus: getActivityStatus(refreshedActivity),
            })

            if (
                refreshedActivity.quiz_status === "passed" ||
                refreshedActivity.quiz_status === "failed"
            ) {
                quizConversationIdRef.current = null
            }
        }
    })

    // Update current activity after activity refresh
    useApiStatus(activitiesService.getActivity, null, false, (data) => {
        if (!quizConversationIdRef.current || data?.id !== activity?.id) {
            return
        }

        setCurrentActivity({
            activityData: data,
            progressStatus: getActivityStatus(data),
        })

        if (data.quiz_status === "passed" || data.quiz_status === "failed") {
            quizConversationIdRef.current = null
        }
    })

    // Clear ref when checkpoint is manually closed
    useEffect(() => {
        if (!checkpointConversation && quizConversationIdRef.current) {
            quizConversationIdRef.current = null
        }
    }, [checkpointConversation])

    // Handler to start the quiz
    const handleStartQuiz = useCallback(async () => {
        setQuizLoading(true)
        try {
            const response = await activitiesService.getPrerequisiteQuiz({
                activityId: activity.id,
            })

            const quizData = response?.data?.data || response?.data

            if (!quizData?.conversation_id || !quizData?.questions?.length) {
                return
            }

            // One attempt per student. The gate normally hides itself once
            // `quiz_status` leaves `pending`, but the activity refresh lands a
            // second or two after the quiz closes — this closes that window
            // rather than letting the student open a quiz the backend refuses.
            if (quizData?.latest_attempt) {
                DialogM.show({
                    text1: "Tu as déjà passé ce quiz",
                    text2: "Tu peux revoir ta correction depuis l'icône en haut de l'activité.",
                    champion: "sam",
                    buttonText1: "OK",
                    exitButton: true,
                    onPressBack: () => {},
                })
                return
            }

            // Check for saved progress
            const savedProgress = quizProgress?.[quizData.conversation_id]
            const questionIndex = savedProgress?.currentQuestionIndex || 0
            const savedAnswers = savedProgress?.answers || []

            const currentQuestion = quizData.questions[questionIndex]
            const questionText = currentQuestion.question_text || ""
            const options = currentQuestion.options || []

            // Format options as proposed responses
            const proposedResponses = options.map((option, index) => ({
                id: index + 1,
                body: option,
                quiz_question_id: currentQuestion.id,
                answer: option,
            }))

            const checkpointData = {
                conversation: {
                    id: quizData.conversation_id,
                    trigger_context: "prerequisite_quiz",
                    status: "ongoing",
                    student_responses_count: savedAnswers.length,
                },
                agent_message: {
                    id: `quiz_question_${currentQuestion.id}`,
                    conversation_id: quizData.conversation_id,
                    body: questionText,
                    proposed_responses: proposedResponses,
                },
                quiz: quizData,
            }

            dispatch(setCheckpointConversation(checkpointData))
        } catch (error) {
            DialogM.show({
                text1: "Erreur",
                text2: "Impossible de charger le quiz. Veuillez réessayer.",
                champion: "abou",
                buttonText1: "OK",
            })
        } finally {
            setQuizLoading(false)
        }
    }, [activity?.id, quizProgress, dispatch])

    // Handler to close the quiz checkpoint
    const handleCloseQuiz = useCallback(() => {
        dispatch(clearCheckpointConversation())
    }, [dispatch])

    // Show quiz gate overlay only when gate is visible and checkpoint is not active
    const showQuizGateOverlay = showQuizGate && !isCheckpointActive

    // Quiz overlay is active when either gate shows or quiz checkpoint is open
    const hasQuizOverlay = showQuizGate || isCheckpointActive

    return {
        // State
        showQuizGate,
        showQuizGateOverlay,
        quizLoading,
        isCheckpointActive,
        hasQuizOverlay,
        checkpointConversation,

        // Handlers
        handleStartQuiz,
        handleCloseQuiz,

        // Props object for QuizGate component
        quizGateProps: showQuizGateOverlay
            ? {
                  onStartQuiz: handleStartQuiz,
                  loading: quizLoading,
              }
            : null,
    }
}
